<template>
  <header class="bg-white shadow-lg fixed w-full z-50 border-b border-green-100">
    <div class="container mx-auto px-6 py-3 flex justify-between items-center">
      <!-- Logo -->
      <router-link to="/" class="text-2xl font-bold text-gray-800 dark:text-white hover:scale-105 transition-transform duration-200">
        <img class="w-36" src="/img/logo-droben.png" alt="Logo">
      </router-link>

      <!-- Navigation -->
      <nav :class="{'hidden': !isOpen, 'flex': isOpen}" class="md:flex font-semibold flex-col md:flex-row absolute md:relative top-full left-0 w-full md:w-auto bg-white md:bg-transparent shadow-lg md:shadow-none border-t md:border-t-0 border-green-50">
        <template v-for="link in navLinks">
          <!-- Regular Links -->
          <a v-if="!link.dropdown && !link.to" :href="link.href" :key="`link-${link.name}`"
            class="text-gray-700 hover:text-green-600 hover:bg-green-50 transition-all duration-200 px-5 py-3 md:py-2 rounded-lg mx-1">
            {{ link.name }}
          </a>

          <!-- RouterLink -->
          <RouterLink v-else-if="link.to" :to="link.to" :key="`router-link-${link.name}`"
            class="text-gray-700 hover:text-green-600 hover:bg-green-50 transition-all duration-200 px-5 py-3 md:py-2 rounded-lg mx-1">
            {{ link.name }}
          </RouterLink>
        </template>

        <!-- Language Switcher -->
        <div class="relative group px-4 py-2 md:py-0">
          <button @click="toggleLanguageDropdown" 
                  class="text-gray-700 hover:text-green-600 transition flex items-center justify-between p-2 rounded-lg hover:bg-green-50"
                  :class="{'bg-green-100 text-green-700': isLanguageDropdownOpen}">
            <div class="flex items-center">
              <img :src="getFlagImage(currentLanguage)" alt="Current language flag" class="w-5 rounded-full h-5 mr-2">
              <span class="text-sm">{{ currentLanguage }}</span>
            </div>
            <svg class="w-4 h-4 ml-1 transform transition-transform" :class="{'rotate-180': isLanguageDropdownOpen}" fill="none" stroke="currentColor" viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>

          <!-- Language Dropdown Menu -->
          <div v-show="isLanguageDropdownOpen" class="md:absolute right-0 mt-1 w-full md:w-48 bg-white border border-gray-200 shadow-xl rounded-lg py-1 z-50">
            <button v-for="lang in languages" :key="lang.code"
              @click="changeLanguage(lang.code)"
              :class="{
                'bg-green-100 text-green-700': currentLanguage === lang.name,
                'text-gray-700 hover:bg-green-50': currentLanguage !== lang.name
              }"
              class="flex items-center w-full text-left px-4 py-2 rounded-md transition-all duration-200 text-sm">
              <img :src="getFlagImage(lang.name)" alt="Language flag" class="w-5 h-5 mr-3 rounded-full">
              {{ lang.name }}
            </button>
          </div>
        </div>
      </nav>

      <!-- Authentication / Logout Button -->
      <div class="flex items-center space-x-4">
        <button v-if="!isAuthenticated" @click="goToAuthPage" class="text-gray-700 hover:text-green-600 hover:bg-green-50 transition-all duration-200 px-5 py-3 md:py-2 rounded-lg mx-1">
          {{ t('header.login') }}
        </button>
        <button v-if="isAuthenticated" @click="logout" class="text-gray-700 hover:text-red-600 hover:bg-green-50 transition-all duration-200 px-5 py-3 md:py-2 rounded-lg mx-1">
          {{ t('header.logout') }}
        </button>
      </div>

      <!-- Mobile Menu Button -->
      <button @click="toggleMenu" class="md:hidden text-gray-700 hover:text-green-600 focus:outline-none p-2 rounded-lg hover:bg-green-50">
        <svg v-if="!isOpen" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16m-7 6h7"></path>
        </svg>
        <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    </div>
  </header>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

const { t, locale } = useI18n();
const currentLanguage = ref('Français');
const isOpen = ref(false);
const isLanguageDropdownOpen = ref(false);
const isAuthenticated = ref(false);

const languages = [
  { name: 'Français', code: 'fr' },
  { name: 'English', code: 'en' },
  { name: 'Español', code: 'es' },
  { name: 'Deutsch', code: 'de' },
  { name: 'العربية', code: 'ar' },
];

const getFlagImage = (languageName) => {
  const flagMap = {
    Français: '/img/fr-flag.png',
    English: '/img/En-flag.png',
    العربية: '/img/ar-flag.png',
    Deutsch: '/img/De-flag.png',
    Español: '/img/sp-flag.png',
  };
  return flagMap[languageName] || '/img/flags/default.png';
};

const toggleMenu = () => {
  isOpen.value = !isOpen.value;
  isLanguageDropdownOpen.value = false;
};

const toggleLanguageDropdown = () => {
  isLanguageDropdownOpen.value = !isLanguageDropdownOpen.value;
};

const changeLanguage = (langCode) => {
  const selectedLang = languages.find((lang) => lang.code === langCode);
  if (selectedLang) {
    localStorage.setItem('lang', langCode);
    locale.value = langCode;
    isLanguageDropdownOpen.value = false;
    window.location.reload();
  }
};

const goToAuthPage = () => {
  window.location.href = '/Auth';
};

const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  isAuthenticated.value = false;
  window.location.reload();
};

onMounted(() => {
  const storedLang = localStorage.getItem('lang') || 'fr';
  const selectedLang = languages.find((lang) => lang.code === storedLang);
  if (selectedLang) {
    currentLanguage.value = selectedLang.name;
    locale.value = storedLang;
  }

  const token = localStorage.getItem('token');
  isAuthenticated.value = token !== null;
});

const navLinks = [
  { name: t('header.home'), to: '/' },
  { name: t('header.products'), href: '/allProduct' },
  // { name: t('header.project'), to: '/Project' },
  { name: t('header.recruitment'), to: '/Recrutment' },
  { name: t('header.contact'), href: '/ContactP' },
];
</script>

<style scoped>
[v-show] {
  transition: all 0.2s ease-out;
}

@media (max-width: 768px) {
  nav {
    transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    max-height: 0;
    overflow: hidden;
  }

  nav.flex {
    max-height: 100vh;
  }
}

.rotate-180 {
  transform: rotate(180deg);
}
</style>